import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { ConfigProvider, theme as antdTheme, App as AntdApp } from 'antd';

type Mode = 'light' | 'dark';
const STORAGE_KEY = 'ddisk.theme';

const ThemeCtx = createContext<{ mode: Mode; toggle: () => void }>({
  mode: 'light',
  toggle: () => {},
});

export const useThemeMode = () => useContext(ThemeCtx);

/** Wraps the app in an Ant Design ConfigProvider with a persisted light/dark toggle. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>(() =>
    localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light',
  );

  // Apply the body background on mount AND on every change — not only on toggle
  // — so a dark-mode reload doesn't flash a light background behind the app.
  // Also stamp the mode on <html> as data-theme so the legacy scoped CSS
  // (.legacy-page, .card, .disk-*) can key its dark overrides off it — Ant's
  // darkAlgorithm only repaints Ant components, not our hand-rolled styles.
  useEffect(() => {
    document.body.style.background = mode === 'dark' ? '#141414' : '#f5f5f0';
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const toggle = () =>
    setMode((m) => {
      const next: Mode = m === 'dark' ? 'light' : 'dark';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });

  const value = useMemo(() => ({ mode, toggle }), [mode]);

  return (
    <ThemeCtx.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: { colorPrimary: '#5b8def', borderRadius: 6 },
        }}
      >
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </ThemeCtx.Provider>
  );
}
